import React from 'react';
import PropTypes from 'prop-types';
import { Box, Grid, Typography, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import SectionContainer from '../components/SectionContainer';
import RevealOnScroll from '../components/RevealOnScroll';

const DEFAULT_STEPS = [
  {
    id: 'step-1',
    number: '01',
    title: 'Tell us about your library',
    description: 'Fill the form below. Takes 2 minutes.',
  },
  {
    id: 'step-2',
    number: '02',
    title: 'We verify and onboard',
    description: 'Our team calls you, verifies details, sets up your library.',
  },
  {
    id: 'step-3',
    number: '03',
    title: 'Your library goes live',
    description: 'Seats, plans, and photos live. Students start booking immediately.',
  },
  {
    id: 'step-4',
    number: '04',
    title: 'You get paid',
    description: 'Funds settled to your wallet. Withdraw anytime.',
  },
];

function HowItWorksSection({ eyebrow, title, subtitle, steps }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const orange = theme.palette.brand.orange.main;
  const golden = theme.palette.brand.golden.main;

  const cardBg = isDark ? 'rgba(27,54,93,0.5)' : theme.palette.background.paper;
  const cardBorder = isDark
    ? '1px solid rgba(255,255,255,0.07)'
    : '1px solid rgba(27,54,93,0.06)';
  const connectorColor = isDark ? 'rgba(255,255,255,0.1)' : 'rgba(27,54,93,0.1)';

  return (
    <SectionContainer
      id="how-it-works"
      eyebrow={eyebrow}
      title={title}
      subtitle={subtitle}
    >
      <Box sx={{ position: 'relative' }}>
        {/* Horizontal connector line on desktop */}
        {isDesktop && (
          <Box
            aria-hidden="true"
            sx={{
              position: 'absolute',
              top: 28,
              left: '12.5%',
              right: '12.5%',
              height: 2,
              background: connectorColor,
              zIndex: 0,
            }}
          />
        )}

        <Grid container spacing={{ xs: 3, md: 3 }} alignItems="flex-start">
          {steps.map((step, i) => (
            <Grid item xs={12} md={3} key={step.id || step.number}>
              <RevealOnScroll delay={i * 0.1}>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'row', md: 'column' },
                    alignItems: { xs: 'flex-start', md: 'center' },
                    gap: { xs: 2, md: 0 },
                    textAlign: { xs: 'left', md: 'center' },
                    position: 'relative',
                    zIndex: 1,
                  }}
                >
                  {/* Step number circle */}
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      flexShrink: 0,
                      borderRadius: '50%',
                      background: `linear-gradient(135deg, ${orange}, ${golden})`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: { xs: 0, md: 3 },
                      boxShadow: `0 8px 24px rgba(242,101,34,0.3)`,
                    }}
                  >
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: 800, color: '#fff', fontSize: '0.95rem' }}
                    >
                      {step.number}
                    </Typography>
                  </Box>

                  {/* Text block */}
                  <Box
                    sx={{
                      p: { xs: 2.5, md: 3 },
                      borderRadius: '16px',
                      background: cardBg,
                      border: cardBorder,
                      width: '100%',
                      boxShadow: isDark
                        ? '0 4px 16px rgba(0,0,0,0.2)'
                        : '0 4px 16px rgba(27,54,93,0.06)',
                    }}
                  >
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: 700, color: 'text.primary', mb: 0.75, lineHeight: 1.3 }}
                    >
                      {step.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.65 }}>
                      {step.description}
                    </Typography>
                  </Box>
                </Box>
              </RevealOnScroll>
            </Grid>
          ))}
        </Grid>
      </Box>
    </SectionContainer>
  );
}

HowItWorksSection.propTypes = {
  eyebrow: PropTypes.string,
  title: PropTypes.string,
  subtitle: PropTypes.string,
  steps: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      number: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      description: PropTypes.string,
    })
  ),
};

HowItWorksSection.defaultProps = {
  eyebrow: 'Simple onboarding',
  title: 'From signup to live in 48 hours.',
  subtitle: 'We handle the setup so you can focus on your students.',
  steps: DEFAULT_STEPS,
};

export default HowItWorksSection;
